import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typography, Box } from '@mui/material';
import PageTemplate from 'src/components/PageTemplate';
import QueryList from 'src/pages/QueryPages/components/QueryList';
import useSWR from 'swr';
import { queryApi } from 'src/api/queryApi';
import { useDecodedToken } from 'src/hooks/useDecodedToken';
import useUsers from 'src/hooks/useUsers';
import useBlacklistedKeywords from 'src/hooks/useBlacklistedKeywords';
import useBlacklistedOutlets from 'src/hooks/useBlacklistedOutlets';
import toast from 'react-hot-toast';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { setActiveStatus } from 'src/store/slices/navbarSlice';
import { useSearchParams, useLocation } from 'react-router-dom';
import moment from 'moment';

// Fetch data for multiple statuses using queryApi
const multiStatusFetcher = async (queryStatuses, queryCount = '250') => {
  // If only one status is provided, fetch directly
  if (queryStatuses.length === 1) {
    return queryApi.fetchQueries(queryStatuses[0], queryCount);
  }

  // For multiple statuses, fetch each status separately and combine results
  const promises = queryStatuses.map((status) => queryApi.fetchQueries(status, queryCount));

  // Wait for all requests to complete
  const results = await Promise.all(promises);

  // Combine all results into a single array
  return results.flat();
};

const sortQueries = (queries, sortOption) => {
  return queries?.sort((a, b) => {
    if (sortOption === 'dueDate') {
      return new Date(a.dueDate) - new Date(b.dueDate);
    }
    return new Date(b.pullTime) - new Date(a.pullTime);
  });
};

const QueryPageTemplate = ({ queryStatuses, title, path, pageType, showDeadline = true, headerSlot }) => {
  // For backward compatibility, accept either queryStatus or queryStatuses
  const queryStatus = Array.isArray(queryStatuses) ? queryStatuses[0] : queryStatuses;

  const { data, error, mutate, isLoading } = useSWR(
    ['queries', Array.isArray(queryStatuses) ? queryStatuses : [queryStatus], '250'],
    ([_, statuses, count]) => multiStatusFetcher(statuses, count),
    { refreshInterval: 1000 }
  );

  const { users, error: usersError } = useUsers();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const [page, setPageState] = useState(1);
  const [sortOption, setSortOption] = useState('dueDate');
  const [selectedHashtags, setSelectedHashtags] = useState(new Set());
  const [isInitialized, setIsInitialized] = useState(false);
  const queriesPerPage = 20;
  const { decodedToken, isDemo } = useDecodedToken();
  const viewMode = useSelector((state) => state.viewMode.mode);
  const theme = useTheme();
  const dispatch = useDispatch();

  // Fetch blacklisted keywords
  const { blacklistedKeywords, isLoading: keywordsLoading, error: keywordsError } = useBlacklistedKeywords();

  // Fetch blacklisted outlets
  const { blacklistedOutlets, isLoading: outletsLoading, error: outletsError } = useBlacklistedOutlets();

  // Initialize page from URL params on mount
  useEffect(() => {
    const pageParam = searchParams.get('page');
    const initialPage = pageParam ? parseInt(pageParam, 10) : 1;
    setPageState(initialPage);
    setIsInitialized(true);
  }, []);

  // Restore scroll position after data is loaded and content is rendered
  useEffect(() => {
    if (!isLoading && data && isInitialized) {
      // Check if we came from navigation state or sessionStorage
      const navigationScrollPos = location.state?.restoreScroll ? location.state.scrollPosition : null;
      const savedScrollPosition = sessionStorage.getItem('queryPageScrollPosition');
      const scrollPos = navigationScrollPos || (savedScrollPosition ? parseInt(savedScrollPosition, 10) : null);

      if (scrollPos) {
        console.log('Restoring scroll position after data load:', scrollPos);

        // Multiple attempts to restore scroll position
        const restoreScroll = () => {
          window.scrollTo({ top: scrollPos, behavior: 'instant' });
          console.log('Attempted scroll restore to:', scrollPos, 'Current position:', window.scrollY);
        };

        // Try immediately
        restoreScroll();

        // Try after a short delay
        setTimeout(restoreScroll, 50);

        // Try after DOM updates
        requestAnimationFrame(() => {
          restoreScroll();
          requestAnimationFrame(restoreScroll);
        });

        // Try after a longer delay to override any router scroll behavior
        setTimeout(restoreScroll, 200);
        setTimeout(restoreScroll, 500);

        // Clean up after final attempt
        setTimeout(() => {
          sessionStorage.removeItem('queryPageScrollPosition');
          console.log('Final scroll position:', window.scrollY);
        }, 1000);
      }
    }
  }, [isLoading, isInitialized, location.state]);

  // Update page state when URL changes (after initialization)
  useEffect(() => {
    if (isInitialized) {
      const pageParam = searchParams.get('page');
      const currentPage = pageParam ? parseInt(pageParam, 10) : 1;
      if (currentPage !== page) {
        setPageState(currentPage);
      }
    }
  }, [searchParams, isInitialized]);

  // Function to update both page state and URL
  const setPage = (newPage) => {
    const isUserInitiated = page !== newPage && isInitialized;
    setPageState(newPage);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (newPage === 1) {
        newParams.delete('page');
      } else {
        newParams.set('page', newPage.toString());
      }
      return newParams;
    });

    // Only scroll to top for user-initiated page changes in grid view
    if (isUserInitiated && viewMode === 'grid') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    // If multiple statuses, use the first one for the active status
    dispatch(setActiveStatus(queryStatus));
    return () => dispatch(setActiveStatus(null));
  }, [dispatch, queryStatus]);

  // Reset page to 1 when hashtag filters change (but only after initialization)
  useEffect(() => {
    if (isInitialized) {
      setPage(1);
    }
  }, [selectedHashtags.size]);

  // Memoized sorting and prioritization
  const sortedQueries = useMemo(() => sortQueries(data, sortOption), [data, sortOption]);

  // Custom sorting to show current user's queries first
  const prioritizedQueries = useMemo(() => {
    return sortedQueries?.sort((a, b) => {
      if (a.userClaimID === decodedToken?.userID && b.userClaimID !== decodedToken?.userID) {
        return -1;
      }
      if (a.userClaimID !== decodedToken?.userID && b.userClaimID === decodedToken?.userID) {
        return 1;
      }
      return 0;
    });
  }, [sortedQueries, decodedToken?.userID]);

  // Function to check if a query contains any blacklisted keywords
  const containsBlacklistedKeywords = useMemo(
    () => (query) => {
      if (!blacklistedKeywords?.length) return false;

      // Combine all text fields that should be checked for blacklisted keywords
      const queryText = [query.title || '', query.description || '', query.mediaOutlet || '', query.queryURL || ''].join(' ').toLowerCase();

      // Check if any blacklisted keyword is found in the query text
      return blacklistedKeywords.some((keyword) => queryText.includes(keyword));
    },
    [blacklistedKeywords]
  );

  // Function to check if a query is from a blacklisted outlet
  const isFromBlacklistedOutlet = useMemo(
    () => (query) => {
      if (!blacklistedOutlets?.length || !query.mediaOutlet) return false;

      // Check if the query's media outlet is in the blacklisted outlets list
      return blacklistedOutlets.some((outlet) => query.mediaOutlet.toLowerCase().trim() === outlet.toLowerCase().trim());
    },
    [blacklistedOutlets]
  );

  // Apply all non-hashtag filters first to create the base dataset for hashtag counting
  const baseFilteredQueries = useMemo(() => {
    const filtered = prioritizedQueries?.filter((query) => {
      // Filter out blacklisted keywords/outlets if status is "new"
      const isNewStatus = Array.isArray(queryStatuses) ? queryStatuses.includes('new') : queryStatus === 'new';

      if (isNewStatus) {
        if (containsBlacklistedKeywords(query) || isFromBlacklistedOutlet(query)) {
          return false;
        }
      }

      // Filter out userClaimID = 1 queries if current user is not user 1
      if (query.userClaimID === 1 && decodedToken?.userID !== 1) {
        return false;
      }

      // Filter out old queries if status is "new"
      if (isNewStatus) {
        const queryDate = moment(query.dueDate);
        const today = moment();
        return queryDate.isAfter(today);
      }

      return true;
    });

    if (isDemo && filtered) {
      return filtered.slice(0, 12);
    }

    return filtered;
  }, [
    prioritizedQueries,
    queryStatuses,
    queryStatus,
    containsBlacklistedKeywords,
    isFromBlacklistedOutlet,
    decodedToken?.userID,
    isDemo,
  ]);

  // Memoized hashtag extraction for filtering performance - only when needed
  const queryHashtagsMap = useMemo(() => {
    if (!baseFilteredQueries || selectedHashtags.size === 0) return new Map();

    const map = new Map();

    // Only extract hashtags for queries when we actually have selected hashtags
    baseFilteredQueries.forEach((query) => {
      if (!query?.id) return;

      const hashtags = [];

      // Fast regex approach for topics field
      if (query.topics && typeof query.topics === 'string') {
        try {
          const hashtagMatches = query.topics.match(/#\w+/g);
          if (hashtagMatches) {
            hashtags.push(...hashtagMatches);
          }
        } catch (e) {
          // Silent fail for malformed data
        }
      }

      // Fast extraction from qwotedTags
      if (query.qwotedTags?.length) {
        query.qwotedTags.forEach((tag) => {
          if (tag?.hashtag?.startsWith?.('#')) {
            hashtags.push(tag.hashtag);
          }
        });
      }

      if (hashtags.length > 0) {
        map.set(query.id, [...new Set(hashtags)]); // Remove duplicates
      }
    });

    return map;
  }, [baseFilteredQueries, selectedHashtags.size]);

  // Apply hashtag filtering on top of base filtered queries
  const filteredQueries = useMemo(() => {
    // If no hashtags are selected, show all base filtered queries (fast path)
    if (selectedHashtags.size === 0) {
      return baseFilteredQueries;
    }

    if (!baseFilteredQueries) return [];

    // Only filter when we have selected hashtags
    return baseFilteredQueries.filter((query) => {
      const queryHashtags = queryHashtagsMap.get(query.id);
      if (!queryHashtags) return false;

      // Check if any selected hashtag is in the query's hashtags
      return Array.from(selectedHashtags).some((hashtag) => queryHashtags.includes(hashtag));
    });
  }, [baseFilteredQueries, selectedHashtags, queryHashtagsMap]);

  const pageCount = useMemo(() => Math.ceil(filteredQueries?.length / queriesPerPage), [filteredQueries?.length, queriesPerPage]);

  // Only paginate in grid view, table view handles its own pagination
  const displayedQueries = useMemo(() => {
    return viewMode === 'grid' ? filteredQueries?.slice((page - 1) * queriesPerPage, page * queriesPerPage) : filteredQueries;
  }, [viewMode, filteredQueries, page, queriesPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleSortChange = (event) => {
    setSortOption(event.target.value);
  };

  if (error || usersError || keywordsError || outletsError) {
    const errorMessage = error
      ? 'Error fetching queries'
      : usersError
      ? 'Error fetching users'
      : keywordsError
      ? 'Error fetching blacklisted keywords'
      : 'Error fetching blacklisted outlets';
    toast.error(errorMessage);
    return <div>Failed to load data</div>;
  }

  return (
    <PageTemplate loading={false}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                letterSpacing: '-0.02em',
                mb: 0.5,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              Manage and overview queries
            </Typography>
          </Box>
          {headerSlot}
        </Box>
        <QueryList
          queries={displayedQueries || []}
          allQueries={baseFilteredQueries} // Pass filtered data for hashtag extraction
          users={users || []}
          path={path}
          currentSearchParams={searchParams.toString()}
          mutate={mutate}
          pageType={pageType}
          sortOption={sortOption}
          onSortChange={handleSortChange}
          page={page}
          pageCount={pageCount}
          onPageChange={handleChangePage}
          showDeadline={showDeadline}
          selectedHashtags={selectedHashtags}
          onHashtagsChange={setSelectedHashtags}
          isLoading={isLoading}
        />
      </Box>
    </PageTemplate>
  );
};

QueryPageTemplate.propTypes = {
  queryStatuses: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
  title: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  pageType: PropTypes.string.isRequired,
  showDeadline: PropTypes.bool,
  headerSlot: PropTypes.node,
};

export default QueryPageTemplate;
